import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowLeft, AlertTriangle } from "lucide-react";
import { passwordError, PW_HINT } from "@/lib/password";

export default function ChangePassword() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.must_change_password;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (passwordError(next)) return setError(passwordError(next));
    if (next !== confirm) return setError("New passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: forced ? undefined : current,
        new_password: next,
      });
      await refreshUser();
      toast.success("Password updated");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative h-screen w-full flex items-center justify-center bg-background text-foreground p-4 scanlines">
      <div className="w-full max-w-sm border border-border bg-surface">
        <div className="border-b border-border px-6 py-5 flex items-center gap-3">
          <div className="h-9 w-9 flex items-center justify-center bg-primary text-primary-foreground">
            <KeyRound className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-sm font-semibold tracking-tight leading-none">Change Password</h1>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">{user?.username}</p>
          </div>
        </div>

        {forced && (
          <div className="mx-6 mt-4 flex items-start gap-2 text-xs text-primary border border-primary/40 bg-primary/10 px-3 py-2" data-testid="forced-notice">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>You must set a new password before continuing.</span>
          </div>
        )}

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {!forced && (
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Current Password</Label>
              <Input data-testid="current-password-input" type="password" value={current}
                onChange={(e) => setCurrent(e.target.value)} required
                className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">New Password</Label>
            <Input data-testid="new-password-input" type="password" value={next}
              onChange={(e) => setNext(e.target.value)} required
              className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Confirm New Password</Label>
            <Input data-testid="confirm-password-input" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} required
              className="font-mono rounded-none bg-background border-border focus-visible:ring-primary" />
          </div>
          <p className="text-[10px] text-muted-foreground">{PW_HINT}</p>
          {error && <p data-testid="change-password-error" className="text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2">{error}</p>}
          <Button type="submit" data-testid="change-password-submit" disabled={loading}
            className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
          </Button>
          {!forced && (
            <button type="button" onClick={() => navigate("/")} className="w-full text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-center gap-1">
              <ArrowLeft className="h-3 w-3" /> Back to Scrubber
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
